import { useCallback, useMemo, useRef, useState } from 'react';

import { StepUpDialogProps } from '~/components/StepUpDialog';
import { StepUpChallenge, StepUpRepository, isStepUpRequiredError } from '~/repositories/StepUpRepository';
import { useNotificationStore } from '~/stores/Notification';

type PendingAction = {
  action: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  error: unknown;
};

const errorMessage = (error: unknown, fallback: string): string =>
  (typeof error === 'object' && error !== null && (error as { message?: string }).message) || fallback;

/**
 * Wraps an API call with the step-up authentication loop: when the call is rejected with
 * step_up_auth_required, begins a step-up challenge, collects the proof via StepUpDialog, and retries
 * the call once the proof is recorded. Render <StepUpDialog {...stepUpDialogProps} /> alongside.
 */
export const useStepUp = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [challenge, setChallenge] = useState<StepUpChallenge | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const pending = useRef<PendingAction | null>(null);

  const runWithStepUp = useCallback(async <T>(action: () => Promise<T>): Promise<T> => {
    try {
      return await action();
    } catch (error) {
      if (!isStepUpRequiredError(error)) {
        throw error;
      }
      const { challengeName } = await StepUpRepository.begin();
      setChallenge(challengeName);
      return new Promise<T>((resolve, reject) => {
        pending.current = { action, resolve: resolve as (value: unknown) => void, reject, error };
      });
    }
  }, []);

  const handleClose = useCallback(() => {
    setChallenge(null);
    if (pending.current) {
      pending.current.reject(pending.current.error);
      pending.current = null;
    }
  }, []);

  const handleSubmit = useCallback(
    async (proof: { code?: string; password?: string }) => {
      if (!pending.current || isVerifying) {
        return;
      }
      setIsVerifying(true);
      try {
        await StepUpRepository.finish(proof);
      } catch (error) {
        addDangerNotification({
          content: errorMessage(error, 'Failed to verify. Please try again.'),
          display: 'popup',
        });
        setIsVerifying(false);
        return;
      }

      const current = pending.current;
      pending.current = null;
      setChallenge(null);
      try {
        current.resolve(await current.action());
      } catch (error) {
        current.reject(error);
      } finally {
        setIsVerifying(false);
      }
    },
    [isVerifying, addDangerNotification]
  );

  const handleResend = useCallback(async () => {
    try {
      // begin re-derives the challenge and re-sends the SMS code.
      await StepUpRepository.begin();
      addSuccessNotification({ content: 'Code resent', display: 'popup' });
    } catch (error) {
      addDangerNotification({ content: errorMessage(error, 'Failed to resend code.'), display: 'popup' });
    }
  }, [addSuccessNotification, addDangerNotification]);

  const stepUpDialogProps: StepUpDialogProps = useMemo(
    () => ({
      isOpen: challenge !== null,
      challenge,
      isLoading: isVerifying,
      onSubmit: handleSubmit,
      onResend: challenge === StepUpChallenge.MfaSms ? handleResend : undefined,
      onClose: handleClose,
    }),
    [challenge, isVerifying, handleSubmit, handleResend, handleClose]
  );

  return { runWithStepUp, stepUpDialogProps };
};
